// The wire shape from /api/aircraft and the SSE stream. Optional fields are genuinely absent,
// not null: the backend drops unknown keys to keep the 5-second payload small (api/schemas.py),
// so `alt === undefined` means "this aircraft is not reporting an altitude".
export type Aircraft = {
  hex: string;
  lat: number;
  lon: number;
  age: number; // seconds, as measured by the server when it sent the frame
  flight?: string;
  reg?: string;
  type?: string; // ICAO type designator, e.g. "B738"
  model?: string; // human model, e.g. "BOEING 737-800"
  alt?: number; // feet
  gs?: number; // knots
  track?: number; // degrees true
  vs?: number; // feet per minute
  squawk?: string;
  cat?: string;
  emg?: string;
  ground?: boolean;
  /** Size rank from the server: 2 airliner, 3 wide-body, 4 four-engine; absent when smaller or unknown. */
  sz?: number;
};

/** One remembered position from the server's downsampled history. */
export type TrackPoint = { ts: string; lat: number; lon: number; alt: number | null };

/** One sampled point of the forward projection; `t` is seconds from now. */
export type ProjectionPoint = { t: number; lat: number; lon: number; alt: number };

/** The full detail payload — nulls are explicit here, unlike the list payload. */
export type AircraftDetail = Aircraft & {
  track_rate?: number | null;
  source?: string;
  seen_pos?: number | null;
  /** Present when the model name came from the learned dictionary, not this frame's feed. */
  model_source?: "remembered";
  /** Size tier used by alert filters — "light" … "four_engine", or null when unknown. */
  size?: string | null;
};

/** A Planespotters photo of this specific airframe. Credit and link are required, not optional. */
export type AircraftPhoto = { url: string; link: string; photographer: string | null };

export type RouteAirport = {
  lat: number;
  lon: number;
  iata: string | null;
  icao: string | null;
  name: string | null;
  city: string | null;
  country: string | null;
};

/** Crowd-sourced; `verified` is the backend's plausibility check. No scheduled times exist. */
export type FlightRoute = {
  airline: string | null;
  origin: RouteAirport;
  destination: RouteAirport;
  verified: boolean;
  remaining_km: number;
  eta_minutes: number | null;
};

/** A rule the selected aircraft is projected to enter, and what (if anything) would silence it. */
export type Outlook = { rule: string; entry_s: number; distance_m: number; blocked_by: string | null };

export type Bbox = { minLat: number; minLon: number; maxLat: number; maxLon: number };

export const bboxParam = (b: Bbox) => `${b.minLat},${b.minLon},${b.maxLat},${b.maxLon}`;

export function bboxContains(outer: Bbox, inner: Bbox): boolean {
  return (
    outer.minLat <= inner.minLat &&
    outer.minLon <= inner.minLon &&
    outer.maxLat >= inner.maxLat &&
    outer.maxLon >= inner.maxLon
  );
}

// Subscribe to more than is on screen, so a small pan doesn't tear down the stream. The margin
// is a trade: too little and every drag reconnects, too much and a zoomed-in user pays for
// aircraft they can't see. 30% of the span is about one screen-width of slack in each direction.
export function padBbox(b: Bbox, fraction = 0.3): Bbox {
  const dLat = (b.maxLat - b.minLat) * fraction;
  const dLon = (b.maxLon - b.minLon) * fraction;
  return {
    minLat: Math.max(-90, b.minLat - dLat),
    minLon: Math.max(-180, b.minLon - dLon),
    maxLat: Math.min(90, b.maxLat + dLat),
    maxLon: Math.min(180, b.maxLon + dLon),
  };
}
